import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Coins,
  FilePlus2,
  History,
  Landmark,
  Scale,
  TrendingUp,
  Wallet2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import {
  CURRENCY,
  JOURNAL_STATUS_BADGE,
  PERIOD_STATUS_BADGE,
  type GlOverviewResponse,
  monthName,
  round2,
} from "@/lib/gl";

function moneyCell(v: number) {
  return `${CURRENCY} ${round2(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function GeneralLedgerOverview() {
  const [data, setData] = useState<GlOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<GlOverviewResponse>("/gl/overview");
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled)
          toast.error((err as Error).message || "Failed to load GL overview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = [
    {
      label: "Total Assets",
      value: moneyCell(data?.kpis.total_assets ?? 0),
      icon: Landmark,
      tone: "from-blue-500 to-cyan-400",
    },
    {
      label: "Total Liabilities",
      value: moneyCell(data?.kpis.total_liabilities ?? 0),
      icon: Wallet2,
      tone: "from-amber-500 to-orange-400",
    },
    {
      label: "Total Equity",
      value: moneyCell(data?.kpis.total_equity ?? 0),
      icon: Coins,
      tone: "from-emerald-500 to-teal-400",
    },
    {
      label: "Net Income (period)",
      value: moneyCell(data?.kpis.net_income_period ?? 0),
      icon: TrendingUp,
      tone: "from-violet-500 to-fuchsia-400",
    },
  ];

  const openPeriod = data?.kpis.open_period;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="shadow-card overflow-hidden">
            <CardContent className="p-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-muted-foreground">{k.label}</div>
                <div className="text-2xl font-bold mt-1 truncate">
                  {loading ? "…" : k.value}
                </div>
              </div>
              <div
                className={`h-12 w-12 rounded-xl bg-gradient-to-br ${k.tone} flex items-center justify-center shadow-card shrink-0`}
              >
                <k.icon className="h-6 w-6 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="shadow-card md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-lg">Latest Posted Journals</CardTitle>
              <CardDescription>
                The five most recently posted journal entries.
              </CardDescription>
            </div>
            <Link
              to="/general-ledger/journals"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : !data?.recent_journals?.length ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No posted journals yet — create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.recent_journals.map((j) => (
                    <TableRow key={j.id}>
                      <TableCell className="font-mono">
                        <Link
                          to={`/general-ledger/journals/${j.id}`}
                          className="text-primary hover:underline"
                        >
                          {j.reference}
                        </Link>
                      </TableCell>
                      <TableCell>{j.entry_date}</TableCell>
                      <TableCell className="truncate max-w-[24ch]">
                        {j.description || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {j.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {moneyCell(j.total_debit)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg">Open Period</CardTitle>
            <CardDescription>The current accounting period.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="text-muted-foreground text-sm">Loading…</div>
            ) : openPeriod ? (
              <>
                <div className="text-2xl font-bold">
                  {monthName(openPeriod.month)} {openPeriod.year}
                </div>
                <div className="text-xs text-muted-foreground">
                  {openPeriod.start_date} → {openPeriod.end_date}
                </div>
                <Badge
                  variant="outline"
                  className={`capitalize ${PERIOD_STATUS_BADGE[openPeriod.status] ?? ""}`}
                >
                  {openPeriod.status}
                </Badge>
                <div className="text-sm text-muted-foreground">
                  Unposted drafts:{" "}
                  <span className="font-semibold text-foreground">
                    {data?.kpis.unposted_journals ?? 0}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button asChild size="sm">
                    <Link to="/general-ledger/journals/new">
                      <FilePlus2 className="h-4 w-4 mr-1" />
                      New Journal
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/general-ledger/reports/trial-balance">
                      <Scale className="h-4 w-4 mr-1" />
                      Trial Balance
                    </Link>
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                No open period — try posting a journal to auto-create one.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />
            Latest Audit Activity
          </CardTitle>
          <CardDescription>
            Append-only trail of GL actions. Click "Audit" tab for full history.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : !data?.recent_audit?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No audit entries yet.
                  </TableCell>
                </TableRow>
              ) : (
                data.recent_audit.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{formatDateTime(a.created_at)}</TableCell>
                    <TableCell>{a.actor_label || "System"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.entity_type}#{a.entity_id}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`capitalize ${
                          JOURNAL_STATUS_BADGE[a.action] ?? ""
                        }`}
                      >
                        {a.action.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
